import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ScrollToTop from './components/ui/ScrollToTop';
import RouteLoadingFallback from './components/ui/RouteLoadingFallback';
import { supabaseConfigMissing } from './lib/supabaseClient';
import PublicLayout, { PublicSiteChrome } from './components/layout/PublicLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { ADMIN_BASE } from './config/adminRoutes';
import {
  Home,
  About,
  ServiceDetail,
  Blog,
  PostDetail,
  Contact,
  Recruitment,
  JobDetail,
  JobApplication,
  SpontaneousApplication,
  PrivacyPolicy,
  TermsOfService,
  NotFound,
  AdminLogin,
  ResetPassword,
  CorporateLayout,
  CorporateDashboard,
  AdminDashboard,
  HRDashboard,
  Employees,
  AdminPosts,
  PostEditorPage,
  CategoryManager,
  MediaLibrary,
  AdminServices,
  AdminJobs,
  AdminApplications,
  AdminMessages,
  AdminNewsletter,
  AuthManager,
  UsersManager,
  DashboardManager,
  RolesManager,
  TicketsManager,
  ChatManager,
  ClientsManager,
  CRMReportsManager,
  AttendanceManager,
  LeaveManager,
  ProductionManager,
  PresenceReportsManager,
  EmployeeList,
  EmployeeProfile,
  ProjectManager,
  LeaveBalances,
  LeavePolicies,
  PerformanceReviews,
  OrgChart,
  Inbox,
  Compose,
  Sent,
  Drafts,
  MailSettings,
  Attachments,
  AnalyticsDashboard,
  HRAnalytics,
  CRMAnalytics,
  AnalyticsReportsManager,
  VisitorAnalytics,
  PowerBIDashboard,
  FinanceBudgets,
  FinanceInvoices,
  FinanceExpenses,
  FinanceReports,
  TechInfra,
  TechSystems,
  TechSecurity,
  TechCompliance,
  CommMarketing,
  CommCampaigns,
  CommDesign,
  IntegrationApis,
  IntegrationLogs,
  IntegrationSync,
  AuditLog,
} from './routes/lazyPages';

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
      {supabaseConfigMissing && process.env.NODE_ENV === 'development' && (
        <div className="bg-amber-500 text-black text-center text-sm py-2 px-4">
          Supabase: configure REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY em .env.local e reinicie npm start.
        </div>
      )}
      <Router>
        <ScrollToTop />
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/servicos" element={<Navigate to="/" replace />} />
            <Route path="/servicos/:slug" element={<ServiceDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<PostDetail />} />
            <Route path="/contato" element={<Contact />} />
            <Route path="/carreira" element={<Recruitment />} />
            <Route path="/carreira/vaga/:id" element={<JobDetail />} />
            <Route path="/carreira/vaga/:id/candidatar" element={<JobApplication />} />
            <Route path="/carreira/candidatura-espontanea" element={<SpontaneousApplication />} />
            <Route path="/recrutamento" element={<Navigate to="/carreira" replace />} />
            <Route path="/recrutamento/vaga/:id" element={<JobDetail />} />
            <Route path="/recrutamento/vaga/:id/candidatar" element={<JobApplication />} />
            <Route path="/recrutamento/candidatura-espontanea" element={<SpontaneousApplication />} />
            <Route path="/horilla" element={<Navigate to="/carreira" replace />} />
            <Route path="/noticias" element={<Blog />} />
            <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
            <Route path="/termos-servico" element={<TermsOfService />} />
          </Route>

          <Route path={`${ADMIN_BASE}/login`} element={<AdminLogin />} />
          <Route path={`${ADMIN_BASE}/redefinir-senha`} element={<ResetPassword />} />

          <Route path={ADMIN_BASE} element={
            <ProtectedRoute>
              <CorporateLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<CorporateDashboard />} />
            <Route path="legacy-dashboard" element={<AdminDashboard />} />

            <Route path="conteudo/posts" element={<AdminPosts />} />
            <Route path="conteudo/posts/novo" element={<PostEditorPage />} />
            <Route path="conteudo/posts/:id" element={<PostEditorPage />} />
            <Route path="conteudo/media" element={<MediaLibrary />} />
            <Route path="conteudo/categorias" element={<CategoryManager />} />
            <Route path="conteudo/servicos" element={<AdminServices />} />
            <Route path="conteudo/vagas" element={<AdminJobs />} />
            <Route path="conteudo/candidaturas" element={<AdminApplications />} />
            <Route path="conteudo/mensagens" element={<AdminMessages />} />
            <Route path="conteudo/newsletter" element={<AdminNewsletter />} />

            <Route path="core/auth" element={<AuthManager />} />
            <Route path="core/users" element={<UsersManager />} />
            <Route path="core/dashboard" element={<DashboardManager />} />
            <Route path="core/roles" element={<RolesManager />} />
            <Route path="horilla/*" element={<Navigate to={`${ADMIN_BASE}/dashboard`} replace />} />

            <Route path="crm/tickets" element={<TicketsManager />} />
            <Route path="crm/chat" element={<ChatManager />} />
            <Route path="crm/clientes" element={<ClientsManager />} />
            <Route path="crm/relatorios" element={<CRMReportsManager />} />

            <Route path="presence/registro" element={<AttendanceManager />} />
            <Route path="presence/producao" element={<ProductionManager />} />
            <Route path="presence/saida" element={<LeaveManager />} />
            <Route path="presence/relatorios" element={<PresenceReportsManager />} />
            <Route path="presenca/registro" element={<AttendanceManager />} />
            <Route path="presenca/colaboradores" element={<EmployeeList />} />
            <Route path="presenca/colaboradores/:id" element={<EmployeeProfile />} />
            <Route path="presenca/producao" element={<ProductionManager />} />
            <Route path="presenca/projectos" element={<ProjectManager />} />
            <Route path="presenca/saida" element={<LeaveManager />} />
            <Route path="presenca/saida/saldos" element={<LeaveBalances />} />
            <Route path="presenca/saida/politicas" element={<LeavePolicies />} />
            <Route path="presenca/avaliacoes" element={<PerformanceReviews />} />
            <Route path="presenca/organograma" element={<OrgChart />} />
            <Route path="presenca/relatorios" element={<PresenceReportsManager />} />

            <Route path="mail/inbox" element={<Inbox />} />
            <Route path="mail/compose" element={<Compose />} />
            <Route path="mail/sent" element={<Sent />} />
            <Route path="mail/draft" element={<Drafts />} />
            <Route path="mail/settings" element={<MailSettings />} />
            <Route path="mail/attachments" element={<Attachments />} />

            <Route path="analytics/visitantes" element={<VisitorAnalytics />} />
            <Route path="analytics/visitantes/:section" element={<VisitorAnalytics />} />
            <Route path="analytics/dashboard" element={<AnalyticsDashboard />} />
            <Route path="analytics/reports" element={<AnalyticsReportsManager />} />
            <Route path="analytics/powerbi" element={<PowerBIDashboard />} />
            <Route path="analytics/rh" element={<HRAnalytics />} />
            <Route path="analytics/crm" element={<CRMAnalytics />} />
            <Route path="analytics/presenca" element={<AnalyticsDashboard />} />
            <Route path="analytics/financeiro" element={<AnalyticsDashboard />} />

            <Route path="financeiro/orcamentos" element={<FinanceBudgets />} />
            <Route path="financeiro/faturacao" element={<FinanceInvoices />} />
            <Route path="financeiro/despesas" element={<FinanceExpenses />} />
            <Route path="financeiro/relatorios" element={<FinanceReports />} />

            <Route path="tecnologia/infraestrutura" element={<TechInfra />} />
            <Route path="tecnologia/sistemas" element={<TechSystems />} />
            <Route path="tecnologia/seguranca" element={<TechSecurity />} />
            <Route path="tecnologia/compliance" element={<TechCompliance />} />

            <Route path="comunicacao/marketing" element={<CommMarketing />} />
            <Route path="comunicacao/campanhas" element={<CommCampaigns />} />
            <Route path="comunicacao/design" element={<CommDesign />} />

            <Route path="integracoes/apis" element={<IntegrationApis />} />
            <Route path="integracoes/logs" element={<IntegrationLogs />} />
            <Route path="integracoes/sincronizacao" element={<IntegrationSync />} />

            <Route path="configuracoes/usuarios" element={<UsersManager />} />
            <Route path="configuracoes/permissoes" element={<RolesManager />} />
            <Route path="configuracoes/auditoria" element={<AuditLog />} />
          </Route>

          <Route path="/corporate" element={
            <ProtectedRoute>
              <CorporateLayout>
                <CorporateDashboard />
              </CorporateLayout>
            </ProtectedRoute>
          } />

          <Route path="/corporate/hr" element={
            <ProtectedRoute>
              <CorporateLayout>
                <HRDashboard />
              </CorporateLayout>
            </ProtectedRoute>
          } />
          <Route path="/corporate/hr/employees" element={
            <ProtectedRoute>
              <CorporateLayout>
                <Employees />
              </CorporateLayout>
            </ProtectedRoute>
          } />

          <Route
            path="*"
            element={
              <PublicSiteChrome>
                <NotFound />
              </PublicSiteChrome>
            }
          />
        </Routes>
        </Suspense>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1f2937',
              color: '#fff',
              border: '1px solid #374151',
            },
            success: {
              style: {
                background: '#065f46',
                color: '#fff',
              },
            },
            error: {
              style: {
                background: '#991b1b',
                color: '#fff',
              },
            },
            loading: {
              style: {
                background: '#1e40af',
                color: '#fff',
              },
            },
          }}
        />
      </Router>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
